import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast from "react-hot-toast";
import {
  PageHeader,
  Card,
  SectionTitle,
  Badge,
  Button,
  Field,
  Input,
  Textarea,
  Sheet,
  Skeleton,
  SkeletonList,
  EmptyState,
  ErrorState,
  Spinner,
  formatDate,
} from "../components/ui";
import { PinIcon, ChevronLeftIcon } from "../components/icons";

const fmtTime = (t) => t || "—";

/* ═════════════════════════════════════════════════════════════
   SITES LIST
═════════════════════════════════════════════════════════════ */

export function Sites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await api.get("/sites/crew");
      setSites(data.data || []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader title="My Sites" subtitle="Assigned work sites" />

      {loading ? (
        <SkeletonList count={3} className="h-40 rounded-2xl" />
      ) : failed ? (
        <ErrorState message="Couldn't load your sites." onRetry={load} />
      ) : sites.length === 0 ? (
        <EmptyState
          icon="📍"
          title="No sites assigned yet"
          hint="Your supervisor will assign you to a site. Check back later."
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <li key={site._id}>
              <Link
                to={`/sites/${site._id}`}
                className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-card transition-all hover:border-brand-300 hover:shadow-lift active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* Wraps to two lines rather than truncating — site names
                        differ in their tail ("… Phase II"), so a hard clip at
                        one line makes two sites indistinguishable. */}
                    <h2 className="line-clamp-2 font-display font-semibold text-slate-900">
                      {site.name}
                    </h2>
                    {site.code && (
                      <p className="truncate font-mono text-xs text-slate-500">{site.code}</p>
                    )}
                  </div>
                  <Badge tone={site.status === "active" ? "green" : "slate"} className="shrink-0">
                    {site.status}
                  </Badge>
                </div>

                {(site.city || site.address) && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-600">
                    <PinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                    <span className="line-clamp-2">
                      {[site.address, site.city, site.state].filter(Boolean).join(", ")}
                    </span>
                  </p>
                )}

                <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
                  {[
                    { label: "Vehicles", n: site.assignedVehicles?.length || 0 },
                    { label: "Instruments", n: site.assignedInstruments?.length || 0 },
                    { label: "Crew", n: site.assignedCrews?.length || 0 },
                  ].map(({ label, n }) => (
                    <div key={label}>
                      <dt className="sr-only">{label}</dt>
                      <dd>
                        <span className="block font-display text-base font-bold text-slate-800 tabular">
                          {n}
                        </span>
                        <span className="block text-[11px] text-slate-500">{label}</span>
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 text-xs text-slate-500">
                  {site.startDate && <span>Start {formatDate(site.startDate)}</span>}
                  {site.endDate && <span>End {formatDate(site.endDate)}</span>}
                  {site.coordinates?.lat != null && (
                    <span className="ml-auto font-medium text-brand-700">● GPS set</span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   MARK LOCATION SHEET

   The crew opens Google Maps, long-presses the exact spot, and pastes
   either the coordinates ("10.627064, 76.197189") — parsed instantly — or
   a share link (maps.app.goo.gl/…), which has no coordinates in it and
   must be redirect-followed server-side.
═════════════════════════════════════════════════════════════ */

function MarkLocationSheet({ site, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [coords, setCoords] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const validate = (lat, lng) => {
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng };
  };

  const parseDirect = (input) => {
    const s = input.trim();
    let m = s.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/[?&]q=(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/);
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/);
    if (m) return validate(+m[1], +m[2]);
    m = s.match(/(-?\d{1,3}\.\d+)[,\s]+(-?\d{1,3}\.\d+)/);
    if (m) return validate(+m[1], +m[2]);
    return null;
  };

  const isShortLink = (s) => /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(s);

  const resolve = async () => {
    setError("");
    const raw = text.trim();
    if (!raw) return;

    const direct = parseDirect(raw);
    if (direct) return setCoords(direct);

    if (isShortLink(raw)) {
      setResolving(true);
      try {
        const { data } = await api.post("/resolve-maps-link", { url: raw });
        const c = validate(data.data?.lat, data.data?.lng);
        if (c) setCoords(c);
        else setError("Couldn't read coordinates from that link.");
      } catch {
        setError(
          "Couldn't resolve that link. Paste the coordinates instead — long-press in Google Maps, then copy the numbers."
        );
      } finally {
        setResolving(false);
      }
      return;
    }

    setError("Couldn't read coordinates. Paste them like: 10.627064, 76.197189");
  };

  const handleChange = (v) => {
    setText(v);
    setError("");
    setCoords(parseDirect(v) || null);
  };

  const handleConfirm = async () => {
    if (!coords) return;
    setSaving(true);
    try {
      await api.post(`/sites/locations/${site._id}/mark-location-crew`, {
        lat: coords.lat,
        lng: coords.lng,
        accuracy: 0,
        source: "manual-map",
      });
      toast.success("Location saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save location");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      title="Mark site location"
      subtitle={site.name}
      onClose={onClose}
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" size="lg" block type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button size="lg" block onClick={handleConfirm} disabled={!coords} loading={saving}>
            {saving ? "Saving…" : "Save location"}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <ol className="space-y-2 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-relaxed text-blue-900">
          <li className="flex gap-2">
            <span className="font-bold">1.</span> Open <b>Google Maps</b> and long-press the exact spot.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">2.</span> Copy the <b>coordinates</b> or the <b>share link</b>.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">3.</span> Paste it below.
          </li>
        </ol>

        <Field label="Coordinates or Google Maps link" htmlFor="loc-input">
          <Input
            id="loc-input"
            type="text"
            inputMode="text"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="10.627064, 76.197189"
            className="font-mono"
          />
        </Field>

        {isShortLink(text) && !coords && (
          <Button variant="secondary" size="md" block onClick={resolve} loading={resolving}>
            {resolving ? "Reading link…" : "Get coordinates from link"}
          </Button>
        )}

        {error && (
          <p role="alert" className="rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
            {error}
          </p>
        )}

        {coords && (
          <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
            <div className="flex items-center gap-2">
              <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-brand-600" />
              <span className="font-mono text-sm font-semibold text-brand-900 tabular">
                {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
              </span>
            </div>
            <a
              href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex h-10 items-center text-sm font-semibold text-brand-800 underline underline-offset-2"
            >
              Verify on map →
            </a>
          </div>
        )}
      </div>
    </Sheet>
  );
}

/* ═════════════════════════════════════════════════════════════
   SITE DETAIL
═════════════════════════════════════════════════════════════ */

export function SiteDetail() {
  const currentMonth = new Date().toISOString().slice(0, 7);

  const { id } = useParams();
  const navigate = useNavigate();

  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState("");

  const [todayRecord, setTodayRecord] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [month, setMonth] = useState(currentMonth);

  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const [showLocationSheet, setShowLocationSheet] = useState(false);

  const loadSite = useCallback(
    () =>
      api
        .get(`/sites/crew/${id}`)
        .then(({ data }) => {
          setSite(data.data);
          setSelectedSite(data.data._id);
        })
        .catch(() => navigate("/sites", { replace: true }))
        .finally(() => setLoading(false)),
    [id, navigate]
  );

  useEffect(() => {
    loadSite();
  }, [loadSite]);

  const fetchTodayStatus = useCallback(async () => {
    if (!selectedSite) return;
    setTodayLoading(true);
    try {
      const { data } = await api.get(`/attendance/crew/today/${selectedSite}`);
      setTodayRecord(data.data);
    } catch {
      setTodayRecord(null);
    } finally {
      setTodayLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    fetchTodayStatus();
  }, [fetchTodayStatus]);

  const fetchHistory = useCallback(async () => {
    if (!selectedSite) return;
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ month, siteId: selectedSite });
      const { data } = await api.get(`/attendance/crew?${params}`);
      setHistory(data.data || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [month, selectedSite]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleCheckIn = async () => {
    if (!selectedSite) return;
    setActionLoading(true);
    try {
      await api.post("/attendance/crew/checkin", { siteId: selectedSite, notes });
      toast.success("Checked in");
      setNotes("");
      await Promise.all([fetchTodayStatus(), fetchHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!selectedSite) return;
    setActionLoading(true);
    try {
      await api.post("/attendance/crew/checkout", { siteId: selectedSite });
      toast.success("Checked out");
      await Promise.all([fetchTodayStatus(), fetchHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-24 rounded-lg" />
        <Skeleton className="h-9 w-56 rounded-lg" />
        <Skeleton className="h-28 rounded-2xl" />
        <Skeleton className="h-52 rounded-2xl" />
      </div>
    );
  }

  if (!site) return null;

  const isCheckedIn = !!todayRecord?.checkIn && !todayRecord?.checkOut;
  const isCompleted = !!todayRecord?.checkOut;
  const hasGPS = site.coordinates?.lat != null && site.coordinates?.lng != null;

  const sections = [
    {
      title: "Vehicles",
      icon: "🚗",
      items: site.assignedVehicles,
      render: (v) => `${v.make} ${v.model} — ${v.regNumber}`,
      empty: "No vehicles assigned",
    },
    {
      title: "Instruments",
      icon: "🔭",
      items: site.assignedInstruments,
      render: (i) => `${i.brand} ${i.model} (${i.type})`,
      empty: "No instruments assigned",
    },
    {
      title: "Crew members",
      icon: "👥",
      items: site.assignedCrews,
      render: (c) => `${c.name} — ${c.role}`,
      empty: "No crew listed",
    },
  ];

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="-ml-2 inline-flex h-11 items-center gap-1 rounded-xl px-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
      >
        <ChevronLeftIcon className="h-4 w-4" />
        Back
      </button>

      <PageHeader
        title={site.name}
        subtitle={site.code}
        action={<Badge tone={site.status === "active" ? "green" : "slate"}>{site.status}</Badge>}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-start">
        {/* ── Main column ──────────────────────────────── */}
        <div className="space-y-5 lg:col-span-2">
          {/* Location */}
          <Card className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="font-display text-sm font-semibold text-slate-900">
                  Site location
                </h2>
                {hasGPS ? (
                  <p className="mt-1 font-mono text-sm text-slate-600 tabular">
                    {site.coordinates.lat.toFixed(5)}, {site.coordinates.lng.toFixed(5)}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">No GPS pin set yet</p>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {hasGPS && (
                  <a
                    href={`https://www.google.com/maps?q=${site.coordinates.lat},${site.coordinates.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-11 items-center rounded-xl border border-slate-300 bg-white px-4 font-display text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                  >
                    Open map
                  </a>
                )}
                <Button variant={hasGPS ? "secondary" : "primary"} onClick={() => setShowLocationSheet(true)}>
                  <PinIcon className="h-4 w-4" />
                  {hasGPS ? "Update" : "Mark location"}
                </Button>
              </div>
            </div>
          </Card>

          {/* Attendance */}
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-display text-sm font-semibold text-slate-900">
                Today's attendance
              </h2>
              {todayRecord && (
                <Badge tone={isCompleted ? "slate" : "green"}>
                  {isCompleted ? "Complete" : "Checked in"}
                </Badge>
              )}
            </div>

            <div className="mt-4">
              {todayLoading ? (
                <Skeleton className="h-12 rounded-xl" />
              ) : todayRecord ? (
                <dl className="flex flex-wrap gap-x-8 gap-y-3">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Check in
                    </dt>
                    <dd className="mt-0.5 font-mono text-lg font-semibold text-slate-900 tabular">
                      {fmtTime(todayRecord.checkIn)}
                    </dd>
                  </div>
                  {todayRecord.checkOut && (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Check out
                      </dt>
                      <dd className="mt-0.5 font-mono text-lg font-semibold text-slate-900 tabular">
                        {fmtTime(todayRecord.checkOut)}
                      </dd>
                    </div>
                  )}
                  {todayRecord.workHours ? (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Hours
                      </dt>
                      <dd className="mt-0.5 font-display text-lg font-bold text-brand-800 tabular">
                        {todayRecord.workHours}h
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : (
                <p className="text-sm text-slate-500">Not checked in at this site today.</p>
              )}
            </div>

            {/* One contextual action instead of two permanently-greyed buttons */}
            <div className="mt-5 space-y-3">
              {!todayRecord && !todayLoading && (
                <>
                  <Textarea
                    rows={2}
                    placeholder="Note (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    aria-label="Check-in note"
                  />
                  <button
                    onClick={handleCheckIn}
                    disabled={actionLoading}
                    className="flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-2xl bg-brand-700 px-6 font-display text-base font-bold text-white shadow-sm transition-colors hover:bg-brand-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-brand-700/60 disabled:active:scale-100 sm:max-w-xs"
                  >
                    {actionLoading && <Spinner className="h-5 w-5" />}
                    {actionLoading ? "Checking in…" : "Check in here"}
                  </button>
                </>
              )}

              {isCheckedIn && (
                <button
                  onClick={handleCheckOut}
                  disabled={actionLoading}
                  className="flex min-h-[56px] w-full items-center justify-center gap-2.5 rounded-2xl bg-red-600 px-6 font-display text-base font-bold text-white shadow-sm transition-colors hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:bg-red-600/60 disabled:active:scale-100 sm:max-w-xs"
                >
                  {actionLoading && <Spinner className="h-5 w-5" />}
                  {actionLoading ? "Checking out…" : "Check out"}
                </button>
              )}

              {isCompleted && (
                <p className="rounded-xl bg-slate-100 px-4 py-3 text-center text-sm font-medium text-slate-600">
                  ✓ Attendance complete for today
                </p>
              )}
            </div>
          </Card>

          {/* Attendance history at this site — previously fetched but never
              rendered anywhere. */}
          <section aria-label="Attendance at this site">
            <SectionTitle
              action={
                <label className="flex items-center gap-2">
                  <span className="sr-only">Month</span>
                  <input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="h-11 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 focus:border-brand-600 focus:outline-none focus:ring-4 focus:ring-brand-600/15"
                  />
                </label>
              }
            >
              Your attendance here
            </SectionTitle>

            {historyLoading ? (
              <SkeletonList count={2} className="h-16 rounded-2xl" />
            ) : history.length === 0 ? (
              <Card className="p-5 text-center text-sm text-slate-500">
                No attendance recorded at this site in {month}.
              </Card>
            ) : (
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {history.map((r) => (
                  <li key={r._id}>
                    <Card className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">
                          {formatDate(r.date + "T00:00:00", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}
                        </p>
                        <p className="mt-0.5 font-mono text-xs text-slate-600 tabular">
                          {fmtTime(r.checkIn)} → {fmtTime(r.checkOut)}
                          {r.workHours ? ` · ${r.workHours}h` : ""}
                        </p>
                      </div>
                      <Badge tone={r.status === "present" ? "green" : "slate"}>{r.status}</Badge>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {/* ── Resources column ─────────────────────────── */}
        <aside className="space-y-5">
          {sections.map(({ title, icon, items, render, empty }) => (
            <Card key={title} className="p-5">
              <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
                <span aria-hidden="true">{icon}</span>
                {title}
                {!!items?.length && (
                  <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 tabular">
                    {items.length}
                  </span>
                )}
              </h2>

              {!items?.length ? (
                <p className="text-sm text-slate-500">{empty}</p>
              ) : (
                <ul className="space-y-2">
                  {items.map((item, i) => (
                    <li
                      key={item._id || i}
                      className="break-anywhere rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm text-slate-700"
                    >
                      {render(item)}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ))}
        </aside>
      </div>

      {showLocationSheet && (
        <MarkLocationSheet
          site={site}
          onClose={() => setShowLocationSheet(false)}
          onSaved={() => {
            setShowLocationSheet(false);
            loadSite();
          }}
        />
      )}
    </div>
  );
}
